"use client";

import { DatadogAppRouter } from "@datadog/browser-rum-nextjs";
import { useEffect, useSyncExternalStore } from "react";
import type {
  DatadogRumContext,
  TelemetryEventName,
  TelemetryProperties,
  TelemetryUser,
} from "../telemetry.types";

type DatadogRumSite =
  | "datadoghq.com"
  | "datadoghq.eu"
  | "us3.datadoghq.com"
  | "us5.datadoghq.com"
  | "ddog-gov.com"
  | "ap1.datadoghq.com"
  | "ap2.datadoghq.com";

const DEFAULT_DATADOG_SITE: DatadogRumSite = "us5.datadoghq.com";
const DEFAULT_SAMPLE_RATE = 100;
const DEFAULT_REPLAY_SAMPLE_RATE = 20;
const MAX_INIT_ATTEMPTS = 3;

let pendingIdentity: { user: TelemetryUser; properties?: TelemetryProperties } | null = null;
let trackingConsentGranted = false;

// Tracks whether `datadogRum.init()` has registered the nextjs plugin. The
// vendor's `DatadogAppRouter` starts a view during render, but only fires the
// (internal, unexported) `startNextjsView` once the plugin is registered -
// otherwise it silently no-ops. `DatadogRumRouter` below remounts the vendor
// component when this flips to true so it retries the view start for the
// current route. Consent matters too: a view started before consent is
// granted is discarded, so readiness also requires consent.
let rumInitialized = false;
const rumReadySubscribers = new Set<() => void>();

function rumReadyKey(): string {
  return rumInitialized && trackingConsentGranted ? "rum-ready" : "rum-pending";
}

function markRumInitialized() {
  if (rumInitialized) return;
  rumInitialized = true;
  notifyRumReadySubscribers();
}

function notifyRumReadySubscribers() {
  for (const subscriber of rumReadySubscribers) subscriber();
}

function subscribeToRumReady(onChange: () => void): () => void {
  rumReadySubscribers.add(onChange);
  return () => rumReadySubscribers.delete(onChange);
}

function setTrackingConsentGranted(granted: boolean) {
  if (trackingConsentGranted === granted) return;
  trackingConsentGranted = granted;
  notifyRumReadySubscribers();
}

function getOptionalEnv(name: string): string | undefined {
  return process.env[name]?.trim() || undefined;
}

function getDatadogConfig(context: DatadogRumContext | undefined) {
  if (
    !context?.applicationId ||
    !context.clientToken ||
    !context.service ||
    !context.env ||
    !context.version
  ) {
    return undefined;
  }

  return {
    applicationId: context.applicationId,
    clientToken: context.clientToken,
    service: context.service,
    site: datadogRumSite(),
    env: context.env,
    version: context.version,
    sessionSampleRate: numberFromEnv(
      "NEXT_PUBLIC_DATADOG_RUM_SESSION_SAMPLE_RATE",
      DEFAULT_SAMPLE_RATE,
    ),
    sessionReplaySampleRate: numberFromEnv(
      "NEXT_PUBLIC_DATADOG_RUM_REPLAY_SAMPLE_RATE",
      DEFAULT_REPLAY_SAMPLE_RATE,
    ),
  };
}

function datadogRumSite(): DatadogRumSite {
  const site = getOptionalEnv("NEXT_PUBLIC_DATADOG_RUM_SITE");
  if (
    site === "datadoghq.com" ||
    site === "datadoghq.eu" ||
    site === "us3.datadoghq.com" ||
    site === "us5.datadoghq.com" ||
    site === "ddog-gov.com" ||
    site === "ap1.datadoghq.com" ||
    site === "ap2.datadoghq.com"
  ) {
    return site;
  }

  return DEFAULT_DATADOG_SITE;
}

function numberFromEnv(name: string, fallback: number): number {
  const value = getOptionalEnv(name);
  if (!value) return fallback;

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;

  return Math.min(Math.max(parsed, 0), 100);
}

function userProperties(
  user: TelemetryUser,
  properties?: TelemetryProperties,
): Record<string, unknown> {
  return {
    ...properties,
    id: user.id,
    clerkId: user.id,
    email: user.email ?? undefined,
    firstName: user.firstName ?? undefined,
    lastName: user.lastName ?? undefined,
    name: user.fullName ?? undefined,
    createdAt: user.createdAt ?? undefined,
    organizationId: user.organization?.id,
    organizationName: user.organization?.name ?? undefined,
    organizationSlug: user.organization?.slug ?? undefined,
    organizationRole: user.organization?.role ?? undefined,
  };
}

export function DatadogRumInit({
  context,
  hasConsent,
}: {
  context?: DatadogRumContext;
  hasConsent: boolean;
}) {
  useEffect(() => {
    let cancelled = false;
    let initAttempts = 0;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;

    async function initializeDatadogRum() {
      const config = getDatadogConfig(context);
      if (!config) return;

      const { datadogRum } = await import("@datadog/browser-rum");
      const { nextjsPlugin } = await import("@datadog/browser-rum-nextjs");
      if (cancelled) return;

      if (datadogRum.getInitConfiguration()) {
        datadogRum.setTrackingConsent(hasConsent ? "granted" : "not-granted");
        setTrackingConsentGranted(hasConsent);
        markRumInitialized();
        if (!hasConsent) {
          pendingIdentity = null;
          datadogRum.clearUser();
          return;
        }
        await applyPendingIdentity();
        return;
      }

      datadogRum.init({
        ...config,
        trackingConsent: hasConsent ? "granted" : "not-granted",
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        plugins: [nextjsPlugin()],
        defaultPrivacyLevel: "mask-user-input",
        proxy: "/api/dd-ingest",
        allowedTracingUrls: [
          {
            match: (url: string) => sameOriginUrl(url),
            propagatorTypes: ["tracecontext"],
          },
        ],
      });
      setTrackingConsentGranted(hasConsent);
      markRumInitialized();
      if (hasConsent) await applyPendingIdentity();
    }

    function scheduleInitializeDatadogRum() {
      initAttempts += 1;

      void initializeDatadogRum().catch((error: unknown) => {
        setTrackingConsentGranted(false);
        console.error("Failed to initialize Datadog RUM", {
          error: error instanceof Error ? error.message : String(error),
        });

        if (!cancelled && initAttempts < MAX_INIT_ATTEMPTS) {
          retryTimeout = setTimeout(scheduleInitializeDatadogRum, initAttempts * 1000);
        }
      });
    }

    scheduleInitializeDatadogRum();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [context, hasConsent]);

  return null;
}

export function DatadogRumRouter() {
  // `DatadogAppRouter` starts a view during render but only once per pathname
  // change (via an internal ref), and view starts silently no-op until RUM
  // finishes initializing. Remounting it when RUM becomes ready resets that
  // ref, so it retries the view start for the current route - fixing pages
  // that never navigate client-side (e.g. /sign-in) and would otherwise never
  // start a view at all.
  const ready = useSyncExternalStore(subscribeToRumReady, rumReadyKey, () => "rum-pending");

  return <DatadogAppRouter key={ready} />;
}

function sameOriginUrl(url: string): boolean {
  if (typeof window === "undefined") return false;

  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

export async function captureDatadogRumAction(
  eventName: TelemetryEventName,
  properties?: TelemetryProperties,
) {
  const { datadogRum } = await import("@datadog/browser-rum");
  if (!datadogRum.getInitConfiguration()) return;

  datadogRum.addAction(eventName, properties);
}

export async function identifyDatadogRumUser(
  user: TelemetryUser,
  properties?: TelemetryProperties,
) {
  pendingIdentity = { user, properties };
  if (!trackingConsentGranted) return;

  await applyPendingIdentity();
}

async function applyPendingIdentity() {
  if (!pendingIdentity) return;

  const { datadogRum } = await import("@datadog/browser-rum");
  if (!datadogRum.getInitConfiguration()) return;

  datadogRum.setUser(userProperties(pendingIdentity.user, pendingIdentity.properties));
}

export async function resetDatadogRumUser() {
  pendingIdentity = null;
  const { datadogRum } = await import("@datadog/browser-rum");
  if (!datadogRum.getInitConfiguration()) return;

  datadogRum.clearUser();
}
